import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { addDays } from 'date-fns';
import { Omit } from 'lodash';
import { retryAfterTimeout, BackendResponse } from '@schenck/rotec-components';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TaskBase, TaskMapper } from './task-base.model';

@Injectable()
export class TaskBaseApi<Task extends TaskBase> {
  constructor(
    protected http: HttpClient,
    protected baseUrl: string
  ) {}

  /**
   * Search for (in)active all tasks in a given time range.
   */
  search$(active: boolean, date: { from: Date; to: Date } | null): Observable<Task[]> {
    return this.http
      .get<
        Task[]
      >(`${this.baseUrl}/${active ? 'active' : 'finished'}?maxResultCount=1000${this.getDate(date)}`)
      .pipe(
        retryAfterTimeout(2000, 5),
        map((tasks) => tasks.map((task) => TaskMapper.mapFromBackend(task)))
      );
  }

  /**
   * Gets a single task.
   */
  get$(id: string): Observable<Task> {
    return this.http.get<Task>(this.baseUrl + '/' + id).pipe(
      retryAfterTimeout(2000, 5, [400, 404]),
      map((task) => TaskMapper.mapFromBackend(task))
    );
  }

  /**
   * Creates a new task.
   */
  create$(task: Omit<Task, 'id'>): Observable<BackendResponse.Response<Task>> {
    return this.http
      .post<Task>(this.baseUrl, TaskMapper.mapToBackend(<Task>task))
      .pipe(BackendResponse.mapToResponse((_task) => TaskMapper.mapFromBackend(_task)));
  }

  /**
   * Uploads given file to server to create multiple tasks at once.
   * Task definitions are read from file.
   */
  createMultipleFromFile$(file: File, assetId: string): Observable<BackendResponse.Response<void>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .post<void>(this.baseUrl + '/fromfile?assetId=' + assetId, formData)
      .pipe(BackendResponse.mapToResponse());
  }

  /**
   * Updates a task.
   */
  update$(task: Task): Observable<BackendResponse.Response<Task | null>> {
    return this.http
      .put<Task | null>(this.baseUrl + '/' + task.id, TaskMapper.mapToBackend(task))
      .pipe(BackendResponse.mapToResponse((_task) => TaskMapper.mapFromBackend(_task)));
  }

  /**
   * Deletes given task.
   */
  delete$(id: string): Observable<BackendResponse.Response<void>> {
    return this.http
      .delete<void>(this.baseUrl + '/' + id)
      .pipe(retryAfterTimeout(2000, 5, [400, 404]), BackendResponse.mapToResponse());
  }

  /**
   * Reactivates a task.
   */
  undo$(id: string): Observable<BackendResponse.Response<void>> {
    return this.http
      .put<void>(this.baseUrl + '/undo/' + id, {})
      .pipe(retryAfterTimeout(2000, 5, [400, 404]), BackendResponse.mapToResponse());
  }

  /**
   * Sets task to done.
   */
  done$(id: string, reason: string): Observable<BackendResponse.Response<void>> {
    return this.http
      .put<void>(`${this.baseUrl}/done/${id}`, { reason })
      .pipe(BackendResponse.mapToResponse());
  }

  /**
   * Shifts task.
   */
  shift$(
    id: string,
    reason: string,
    shiftedDueDate: Date
  ): Observable<BackendResponse.Response<void>> {
    return this.http
      .put<void>(`${this.baseUrl}/shift/${id}`, {
        reason,
        shiftedDueDate: TaskMapper.stringifyDate(shiftedDueDate),
      })
      .pipe(BackendResponse.mapToResponse());
  }

  /**
   * Skips task.
   */
  skip$(id: string, reason: string): Observable<BackendResponse.Response<void>> {
    return this.http
      .put<void>(`${this.baseUrl}/skip/${id}`, { reason })
      .pipe(BackendResponse.mapToResponse());
  }

  /**
   * Sets documents only relevant for this single task.
   */
  setDocuments$(id: string, fileIds: string[]): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/documents/${id}`, { ids: fileIds });
  }

  /**
   * Transform date range object to a query string.
   */
  private getDate(date?: { from: Date; to: Date }) {
    if (!date) {
      return '';
    }

    let queryString = '';

    if (date.from) {
      queryString += '&begin=' + date.from.toISOString();
    }

    if (date.to) {
      // When, in the frontend, the user selects "find everything until day X", we to find everything until day X 23:59h.
      // Therefore we add one day before sending the filter to the backend.
      queryString += '&end=' + addDays(date.to, 1).toISOString();
    }

    return queryString;
  }
}
